import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Share2, Building2, User, FileText, Plus, Loader2, X, Maximize2, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { api, errMsg, eur } from '../api';
import { PageHeader, Card, Empty, RatingBadge, goldBtn, ghostBtn } from '../ui';
import PurchaseDialog from '../PurchaseDialog';
import { usePortal } from '../PortalAuth';
import GraphCanvas from '../graph/GraphCanvas';
import GraphColorLegend from '../graph/GraphColorLegend';
import ForeignLookup from '../graph/ForeignLookup';
import { useSecondaryFilter, SecondaryToggle } from '../graph/SecondaryFilter';
import { GraphStyleSelect, GroupPanel, useGraphStyle } from '../graph/GraphStyle';

const EDGE_COLORS = { shareholder: '#D4AF37', director: '#6FA8DC', subsidiary: '#5FC18F', parent: '#C792EA', affiliate: '#8A8F97' };
const EDGE_LABELS = { shareholder: 'Socio', director: 'Dirigente', subsidiary: 'Partecipata', parent: 'Controllante', affiliate: 'Collegata' };

function mergeGraph(prev, g) {
  const nodes = new Map(prev.nodes.map((n) => [n.id, n]));
  const alias = new Map(prev.nodes.filter((n) => n.connect_id && n.connect_id !== n.id).map((n) => [n.connect_id, n.id]));
  const id = (x) => alias.get(x) || x;
  g.nodes.forEach((raw) => {
    const n = { ...raw, id: id(raw.id) };
    const ex = nodes.get(n.id);
    nodes.set(n.id, ex ? Object.assign(ex, { ...n, label: ex.label || n.label, root: ex.root || false, expanded: ex.expanded || n.root }) : { ...n, expanded: !!n.root, root: prev.nodes.length === 0 && n.root });
  });
  const key = (l) => `${l.source.id || l.source}|${l.target.id || l.target}|${l.type}`;
  const links = [...prev.links];
  const seen = new Set(links.map(key));
  g.edges.forEach((raw) => { const e = { ...raw, source: id(raw.source), target: id(raw.target) }; const k = key(e); if (!seen.has(k)) { seen.add(k); links.push(e); } });
  return { nodes: [...nodes.values()], links };
}

function PersonActions({ node, info, onSearch, onDirectorReport, busy }) {
  if (!info) {
    return <Button onClick={() => onSearch(node)} disabled={busy} className={`${goldBtn} h-10 w-full`} data-testid="graph-search-person">{busy ? <Loader2 className="animate-spin" size={15} /> : <><Plus size={15} className="mr-2" />Cerca altre cariche</>}</Button>;
  }
  return (
    <div className="space-y-3" data-testid="graph-person-result">
      <p className="text-xs text-[#A3A3A3]">{info.companies} società collegate trovate {info.has_director_report ? 'dal report dirigente e dai tuoi report' : 'nei tuoi report'}.</p>
      {!info.has_director_report && info.director_report_enabled && (
        <Button onClick={() => onDirectorReport(node)} disabled={busy} className={`${ghostBtn} h-10 w-full`} data-testid="graph-director-report">
          {busy ? <Loader2 className="animate-spin" size={15} /> : `Tutte le cariche in Italia · ${eur(info.director_report_price_cents)}`}
        </Button>
      )}
      {!info.has_director_report && !info.director_report_enabled && (
        <p className="text-xs text-[#7A7F87] border border-[#1F242C] p-3 rounded-sm" data-testid="graph-director-report-disabled">Elenco completo delle cariche (report dirigente): si attiverà automaticamente non appena sarà disponibile.</p>
      )}
    </div>
  );
}

function NodePanel({ node, graph, onExpand, onLookup, onClose, busy, navigate, personInfo, onSearchPerson, onDirectorReport, recurring }) {
  if (node.members) return <GroupPanel group={node} onClose={onClose} />;
  const rels = graph.links.filter((l) => (l.source.id || l.source) === node.id || (l.target.id || l.target) === node.id);
  const nameOf = (id) => graph.nodes.find((n) => n.id === id)?.label || id;
  return (
    <Card className="p-6 absolute top-4 right-4 w-[min(360px,calc(100%-2rem))] max-h-[calc(100%-2rem)] overflow-y-auto bg-[#0B0D10]/95 backdrop-blur-xl z-10" data-testid="graph-node-panel">
      <button onClick={onClose} className="absolute top-4 right-4 text-[#5A5F67] hover:text-white" data-testid="graph-panel-close"><X size={16} /></button>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#D4AF37] mb-3">{node.type === 'person' ? <User size={13} /> : <Building2 size={13} />}{node.type === 'person' ? 'Persona fisica' : 'Società'}</div>
      <p className="font-heading text-lg text-[#F2F2F2] mb-2 pr-6" data-testid="graph-panel-name">{node.label}</p>
      <p className="text-xs font-mono text-[#5A5F67] mb-4 break-all">{node.connect_id || node.vat || node.tax_code}</p>
      {node.rating && <div className="flex items-center gap-3 mb-4"><RatingBadge value={node.rating} /><span className="text-sm text-[#A3A3A3]">{node.rating_desc}</span></div>}
      {node.address && <p className="text-sm text-[#8A8F97] mb-2">{node.address}</p>}
      {node.birth && <p className="text-sm text-[#8A8F97] mb-2">Nato/a il {new Date(node.birth).toLocaleDateString('it-IT')}</p>}
      {node.negative && <p className="text-xs text-[#E37A72] mb-2">Segnalate negatività (protesti/pregiudizievoli)</p>}
      {recurring?.has(node.id) && <p className="text-xs text-[#D4AF37] mb-2 border border-[#D4AF37] px-2 py-1 rounded-sm w-fit" data-testid="graph-panel-recurring">Presente in {recurring.get(node.id)} società del grafo</p>}
      <p className="text-[11px] uppercase tracking-[0.15em] text-[#5A5F67] mt-5 mb-2">Relazioni nel grafo ({rels.length})</p>
      <div className="space-y-2 mb-5">
        {rels.map((l, i) => {
          const s = l.source.id || l.source; const t = l.target.id || l.target;
          const other = s === node.id ? t : s;
          return <div key={i} className="text-xs flex gap-2"><span className="w-2 h-2 mt-1 shrink-0" style={{ background: EDGE_COLORS[l.type] }} /><span className="text-[#A3A3A3]">{l.label}</span><span className="text-[#E5E5E5] truncate">{s === node.id ? '→' : '←'} {nameOf(other)}</span></div>;
        })}
      </div>
      {node.type === 'company' && (
        <div className="flex flex-col gap-2">
          {!node.expanded && (node.connect_id || node.vat || node.safe_number) && <Button onClick={() => onExpand(node)} disabled={busy} className={`${goldBtn} h-10`} data-testid="graph-expand-btn">{busy ? <Loader2 className="animate-spin" size={15} /> : <><Plus size={15} className="mr-2" />Espandi relazioni</>}</Button>}
          {!node.expanded && !node.connect_id && !node.vat && !node.safe_number && (<>
            <p className="text-xs text-[#8A8F97] border border-[#1F242C] p-3 rounded-sm" data-testid="graph-foreign-note">Società non collegata nella banca dati (spesso estera): cercala e richiedi il report per espandere il grafo.</p>
            <Button onClick={() => onLookup(node)} disabled={busy} className={`${goldBtn} h-10`} data-testid="graph-foreign-lookup-btn"><Globe size={15} className="mr-2" />Cerca e richiedi report</Button>
          </>)}
          {node.report_id && <Button onClick={() => navigate(`/portal/report/${node.report_id}`)} className={`${ghostBtn} h-10`} data-testid="graph-open-report"><FileText size={15} className="mr-2" />Apri report</Button>}
        </div>
      )}
      {node.type === 'person' && node.tax_code && <PersonActions node={node} info={personInfo[node.tax_code]} onSearch={onSearchPerson} onDirectorReport={onDirectorReport} busy={busy} />}
    </Card>
  );
}

export default function GraphPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const box = useRef(null);
  const fg = useRef(null);
  const [graphStyle, setGraphStyle] = useGraphStyle();
  const [groups, setGroups] = useState([]);
  const [graph, setGraph] = useState({ nodes: [], links: [] });
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [buying, setBuying] = useState(null);
  const [lookup, setLookup] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const { refresh } = usePortal();

  const [personInfo, setPersonInfo] = useState({});
  const add = useCallback((g) => { setGraph((p) => mergeGraph(p, g)); fg.current?.fit(); }, []);

  const loadCompany = useCallback(async (companyId, name) => {
    const { data } = await api.get(`/cs/graph/company/${companyId}`);
    if (data.needs_purchase) { setBuying({ id: companyId, name: name || companyId, price: data.price_cents }); return false; }
    add(data); return true;
  }, [add]);

  useEffect(() => {
    const rep = params.get('report'); const comp = params.get('company'); const person = params.get('person');
    (async () => {
      try {
        if (rep) add((await api.get(`/cs/reports/${rep}/graph`)).data);
        else if (comp) await loadCompany(comp);
        else if (person) {
          const { data } = await api.get(`/cs/graph/person/${person.toUpperCase()}`);
          add({ nodes: data.nodes, edges: data.edges });
          setPersonInfo((p) => ({ ...p, [person.toUpperCase()]: data }));
        }
      } catch (e) { toast.error(errMsg(e)); } finally { setLoaded(true); }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const expand = async (node) => {
    setBusy(true);
    try {
      let cid = node.connect_id;
      if (!cid && node.vat) { const { data } = await api.get('/cs/graph/resolve', { params: { vat: node.vat } }); cid = data.id; }
      if (!cid && node.safe_number) { const { data } = await api.get('/cs/graph/resolve', { params: { safe: node.safe_number } }); cid = data.id; }
      if (!cid) { toast.error('Identificativo della società non disponibile'); return; }
      setGraph((p) => ({ ...p, nodes: p.nodes.map((n) => (n.id === node.id ? Object.assign(n, { connect_id: cid }) : n)) }));
      const ok = await loadCompany(cid, node.label);
      if (ok) setGraph((p) => ({ ...p, nodes: p.nodes.map((n) => (n.id === node.id ? Object.assign(n, { expanded: true }) : n)) }));
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const applyPerson = (node, data) => {
    add({ nodes: data.nodes, edges: data.edges });
    setPersonInfo((p) => ({ ...p, [node.tax_code]: data }));
    setGraph((p) => ({ ...p, nodes: p.nodes.map((n) => (n.id === node.id ? Object.assign(n, { expanded: true }) : n)) }));
  };
  const searchPerson = async (node) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/cs/graph/person/${node.tax_code}`);
      applyPerson(node, data);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const directorReport = async (node) => {
    const info = personInfo[node.tax_code];
    if (!window.confirm(`Acquistare il report esponente di ${node.label} per ${eur(info?.director_report_price_cents)}? Mostra tutte le cariche attuali e passate.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/cs/graph/person/${node.tax_code}/director-report`);
      applyPerson(node, data);
      refresh();
      toast.success(`Report esponente acquistato: ${data.companies} società collegate`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const recurring = useMemo(() => {
    const types = new Map(graph.nodes.map((n) => [n.id, n.type]));
    const m = new Map();
    graph.links.forEach((l) => {
      const s = l.source.id || l.source; const t = l.target.id || l.target;
      [[s, t], [t, s]].forEach(([p, c]) => {
        if (types.get(p) === 'person' && types.get(c) === 'company') m.set(p, (m.get(p) || new Set()).add(c));
      });
    });
    return new Map([...m].filter(([, set]) => set.size > 1).map(([p, set]) => [p, set.size]));
  }, [graph]);

  const rootId = useMemo(() => graph.nodes.find((n) => n.root)?.id || graph.nodes[0]?.id, [graph.nodes]);
  const pick = (id) => setSelected(graph.nodes.find((n) => n.id === id) || groups.find((g) => g.id === id) || null);
  const view = useSecondaryFilter(graph.nodes, graph.links, rootId);


  const legend = useMemo(() => Object.entries(EDGE_LABELS), []);

  return (
    <div>
      <PageHeader kicker="Ricerca tramite grafo" title="Grafo delle relazioni">
        <SecondaryToggle filter={view} />
        <GraphStyleSelect value={graphStyle} onChange={setGraphStyle} />
        <Button onClick={() => fg.current?.fit()} className={`${ghostBtn} h-10`} data-testid="graph-fit"><Maximize2 size={15} className="mr-2" />Adatta</Button>
        <Button onClick={() => { setGraph({ nodes: [], links: [] }); setSelected(null); navigate('/portal/grafo'); }} className={`${ghostBtn} h-10`} data-testid="graph-reset">Svuota</Button>
      </PageHeader>
      <Card className="relative h-[70vh] min-h-[480px] overflow-hidden portal-grid-bg" >
        <div ref={box} className="absolute inset-0" data-testid="graph-canvas">
          {graph.nodes.length > 0 && (
            <GraphCanvas ref={fg} root={rootId} nodes={view.nodes} links={view.links} styleId={graphStyle} onSelect={pick} onGroups={setGroups} />
          )}
        </div>
        {loaded && graph.nodes.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Empty icon={Share2} title="Parti da un'azienda" text="Apri il grafo da un report acquistato o dalla ricerca aziende. Clicca un nodo per vederne le relazioni ed espandere soci, controllate e partecipate.">
              <Button onClick={() => navigate('/portal/ricerca')} className={`${goldBtn} h-10`} data-testid="graph-go-search">Vai alla ricerca</Button>
            </Empty>
          </div>
        )}
        {graph.nodes.length > 0 && (
          <div className="absolute bottom-4 left-4 flex flex-wrap gap-3 bg-[#050505]/80 backdrop-blur px-4 py-3 border border-[#1F242C] text-xs" data-testid="graph-legend">
            <span className="flex items-center gap-2 text-[#A3A3A3]"><span className="w-2.5 h-2.5 border border-[#D4AF37]" />Società</span>
            <span className="flex items-center gap-2 text-[#A3A3A3]"><span className="w-2.5 h-2.5 rounded-full border border-[#6FA8DC]" />Persona</span>
            {legend.map(([k, l]) => <span key={k} className="flex items-center gap-2 text-[#A3A3A3]"><span className="w-4 h-0.5" style={{ background: EDGE_COLORS[k] }} />{l}</span>)}
            <span className="text-[#5A5F67] font-mono">{graph.nodes.length} nodi · {graph.links.length} relazioni</span>
          </div>
        )}
        {selected && <NodePanel node={selected} graph={graph} onExpand={expand} onLookup={setLookup} onClose={() => setSelected(null)} busy={busy} navigate={navigate} personInfo={personInfo} onSearchPerson={searchPerson} onDirectorReport={directorReport} recurring={recurring} />}
      </Card>
      <ForeignLookup node={lookup} onClose={() => setLookup(null)} onPick={(c) => { const n = lookup; setLookup(null); setGraph((p) => ({ ...p, nodes: p.nodes.map((x) => (x.id === n.id ? Object.assign(x, { connect_id: c.id }) : x)) })); loadCompany(c.id, c.name).catch((e) => toast.error(errMsg(e))); }} />
      {graph.nodes.some((n) => n.ubo) && <GraphColorLegend />}
      {buying && <p className="sr-only">{eur(buying.price)}</p>}
      <PurchaseDialog company={buying} open={!!buying} onClose={() => setBuying(null)} defaultTemplate="complete" lockTemplate
        onDone={async (doc) => { setBuying(null); add((await api.get(`/cs/reports/${doc.id}/graph`)).data); setGraph((p) => ({ ...p, nodes: p.nodes.map((n) => ((n.connect_id === doc.company_id) ? Object.assign(n, { expanded: true, report_id: doc.id }) : n)) })); }} />
    </div>
  );
}
